import { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import { api, fetcher } from '../lib/api';
import { useSocket } from './useSocket';
import type {
  HealthResponse,
  QueueStatsResponse,
  DocumentStatsResponse,
  ClassifierStatsResponse,
  ClassifierErrorsResponse,
  RateLimitResponse,
  IconectStatsResponse,
  OneDriveStatsResponse,
  PhiStatsResponse,
  BatchesResponse,
  K8sWorkersResponse,
  K8sVllmResponse,
  UsageMetricsResponse,
} from '../types';

// Refresh intervals for polling fallback (ms)
const POLL_INTERVAL_FAST = 2000;
const POLL_INTERVAL_SLOW = 5000;

export function useHealth() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<HealthResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<HealthResponse>(
    '/health',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_SLOW,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('health', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useQueueStats() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<QueueStatsResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<QueueStatsResponse>(
    '/api/stats/queue?sampleSize=100',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:queue', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useBatches() {
  const { data, error, isLoading, mutate } = useSWR<BatchesResponse>(
    '/api/batches',
    fetcher,
    {
      refreshInterval: POLL_INTERVAL_SLOW,
      revalidateOnFocus: false,
    }
  );

  return {
    data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useDocumentStats(params?: { addedBy?: string; batchName?: string }) {
  // Document stats always uses HTTP polling because:
  // 1. It supports batch filtering which requires query params
  // 2. The aggregation query is relatively expensive, so we poll at a slower rate
  const queryString = new URLSearchParams();
  if (params?.addedBy) queryString.set('addedBy', params.addedBy);
  if (params?.batchName) queryString.set('batchName', params.batchName);
  const query = queryString.toString();

  const { data, error, isLoading, mutate } = useSWR<DocumentStatsResponse>(
    `/api/stats/documents${query ? `?${query}` : ''}`,
    fetcher,
    {
      refreshInterval: POLL_INTERVAL_SLOW,
      revalidateOnFocus: false,
    }
  );

  return {
    data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useClassifierStats() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<ClassifierStatsResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<ClassifierStatsResponse>(
    '/api/stats/classifier',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:classifier', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  const reset = useCallback(async () => {
    await api.resetClassifierStats();
    mutate();
  }, [mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
    reset,
  };
}

export function useRateLimits() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<RateLimitResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<RateLimitResponse>(
    '/api/rate-limits',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:rateLimits', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useClassifierErrors(params?: { batchName?: string }) {
  // Classifier errors always uses HTTP polling because:
  // 1. It supports batch filtering which requires query params
  // 2. The query involves scanning documents with errors
  const queryString = new URLSearchParams();
  queryString.set('limit', '10');
  if (params?.batchName) queryString.set('batchName', params.batchName);
  const query = queryString.toString();

  const { data, error, isLoading, mutate } = useSWR<ClassifierErrorsResponse>(
    `/api/stats/classifier-errors?${query}`,
    fetcher,
    {
      refreshInterval: POLL_INTERVAL_SLOW,
      revalidateOnFocus: false,
    }
  );

  return {
    data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useIconectStats() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<IconectStatsResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<IconectStatsResponse>(
    '/api/stats/iconect',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:iconect', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  const reset = useCallback(async () => {
    await api.resetIconectStats();
    mutate();
  }, [mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
    reset,
  };
}

export function useOneDriveStats() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<OneDriveStatsResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<OneDriveStatsResponse>(
    '/api/stats/onedrive',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:oneDrive', (newData: OneDriveStatsResponse) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  const reset = useCallback(async () => {
    await api.resetOneDriveStats();
    mutate();
  }, [mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
    reset,
  };
}

export function useK8sWorkers() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<K8sWorkersResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<K8sWorkersResponse>(
    '/api/k8s/workers',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : 10000, // Poll every 10s if no socket
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:k8sWorkers', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function useK8sVllm() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<K8sVllmResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<K8sVllmResponse>(
    '/api/k8s/vllm',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : 10000,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:k8sVllm', (newData) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

export function usePhiStats() {
  const { isConnected, subscribe } = useSocket();
  const [socketData, setSocketData] = useState<PhiStatsResponse | null>(null);

  const { data, error, isLoading, mutate } = useSWR<PhiStatsResponse>(
    '/api/stats/phi',
    fetcher,
    {
      refreshInterval: isConnected ? 0 : POLL_INTERVAL_FAST,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    return subscribe('stats:phi', (newData: PhiStatsResponse) => {
      setSocketData(newData);
      mutate(newData, false);
    });
  }, [subscribe, mutate]);

  const reset = useCallback(async () => {
    await api.resetPhiStats();
    mutate();
  }, [mutate]);

  return {
    data: socketData || data,
    error,
    isLoading,
    refresh: () => mutate(),
    reset,
  };
}

// Phase 6 metering — cross-org usage stats. Polling-only (60s); no WS pair.
export interface UsageMetricsParams {
  range?: '1h' | '24h' | '7d' | '30d' | '90d';
  from?: string;
  to?: string;
  granularity?: 'hour' | 'day';
  organizationId?: string;
  userId?: string;
  classifierKey?: string;
  service?: string;
}

export function useUsageMetrics(
  params: UsageMetricsParams | '1h' | '24h' | '7d' | '30d' | '90d' = '30d',
) {
  const p: UsageMetricsParams =
    typeof params === 'string' ? { range: params } : params;

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const url = `/api/stats/usage?${qs.toString()}`;

  const { data, error, isLoading, mutate } = useSWR<UsageMetricsResponse>(
    url,
    fetcher,
    {
      refreshInterval: 60000,
      revalidateOnFocus: false,
    }
  );

  return {
    data: data ?? null,
    error,
    isLoading,
    refresh: () => mutate(),
  };
}

// Combined hook for all dashboard stats with batch filtering support
export function useDashboardStats(batchFilter?: string | null) {
  const health = useHealth();
  const queueStats = useQueueStats();
  const batches = useBatches();

  // These support batch filtering
  const documentStats = useDocumentStats(batchFilter ? { batchName: batchFilter } : undefined);
  const classifierErrors = useClassifierErrors(batchFilter ? { batchName: batchFilter } : undefined);

  // These are process-level, not batch-filterable
  const classifierStats = useClassifierStats();
  const iconectStats = useIconectStats();
  const oneDriveStats = useOneDriveStats();
  const phiStats = usePhiStats();
  const k8sWorkers = useK8sWorkers();
  const k8sVllm = useK8sVllm();
  const { isConnected } = useSocket();

  const refreshAll = useCallback(() => {
    health.refresh();
    queueStats.refresh();
    batches.refresh();
    documentStats.refresh();
    classifierStats.refresh();
    classifierErrors.refresh();
    iconectStats.refresh();
    oneDriveStats.refresh();
    phiStats.refresh();
    k8sWorkers.refresh();
    k8sVllm.refresh();
  }, [health, queueStats, batches, documentStats, classifierStats, classifierErrors, iconectStats, oneDriveStats, phiStats, k8sWorkers, k8sVllm]);

  const isLoading =
    health.isLoading ||
    queueStats.isLoading ||
    batches.isLoading ||
    documentStats.isLoading ||
    classifierStats.isLoading ||
    classifierErrors.isLoading ||
    iconectStats.isLoading;
    // Note: k8sWorkers.isLoading, oneDriveStats.isLoading, and phiStats.isLoading not included to avoid blocking initial load

  const error =
    health.error ||
    queueStats.error ||
    batches.error ||
    documentStats.error ||
    classifierStats.error ||
    classifierErrors.error ||
    iconectStats.error;
    // Note: k8sWorkers.error, oneDriveStats.error, and phiStats.error not included - they're optional

  return {
    health: health.data,
    queueStats: queueStats.data,
    batches: batches.data,
    documentStats: documentStats.data,
    classifierStats: classifierStats.data,
    classifierErrors: classifierErrors.data,
    iconectStats: iconectStats.data,
    oneDriveStats: oneDriveStats.data,
    phiStats: phiStats.data,
    k8sWorkers: k8sWorkers.data,
    k8sVllm: k8sVllm.data,
    isConnected,
    isLoading,
    error,
    refreshAll,
    resetClassifierStats: classifierStats.reset,
    resetIconectStats: iconectStats.reset,
    resetOneDriveStats: oneDriveStats.reset,
    resetPhiStats: phiStats.reset,
  };
}
